/**
 * A1: how a build of the staff app signs in, fixed when it is built.
 * - cookie (the default): the web app, served from the API's own origin; the
 *   browser keeps the httpOnly cookies and the app never sees a credential.
 * - token: the iPad app (A2), whose pages run at their own origin; the API is
 *   another site, so the credentials travel in headers to `VITE_API_ORIGIN`.
 *
 * Read by vite.config.ts, so a bad setting stops the build, and by api.ts.
 * Plain values in, no `import.meta` or `window`: it runs in Node too.
 */
export type AuthBuild = { mode: 'cookie' } | { mode: 'token'; apiOrigin: string }

/** Loopback only: a build pointed at it is for tests on this machine, never a device. */
const LOOPBACK = new Set(['localhost', '127.0.0.1'])

export function authBuild(env: Readonly<Record<string, string | undefined>>): AuthBuild {
  const mode = env.VITE_STAFF_AUTH || 'cookie'
  const origin = env.VITE_API_ORIGIN || undefined
  if (mode === 'cookie') {
    if (origin) {
      throw new Error(
        'VITE_API_ORIGIN is for the token build only: the web app calls the API on its own origin',
      )
    }
    return { mode: 'cookie' }
  }
  if (mode !== 'token') {
    throw new Error(`VITE_STAFF_AUTH must be "cookie" or "token", not "${mode}"`)
  }
  if (!origin) throw new Error('the token build needs VITE_API_ORIGIN: where the API answers')
  let url: URL
  try {
    url = new URL(origin)
  } catch {
    throw new Error(`VITE_API_ORIGIN is not a URL: "${origin}"`)
  }
  if (url.origin !== origin) {
    throw new Error(
      `VITE_API_ORIGIN must be an origin alone, like https://api.example.org: not "${origin}"`,
    )
  }
  // The credentials cross the network in these headers: never in clear text.
  const loopback = url.protocol === 'http:' && LOOPBACK.has(url.hostname)
  if (url.protocol !== 'https:' && !loopback) {
    throw new Error(`VITE_API_ORIGIN must use https (http only on loopback): "${origin}"`)
  }
  return { mode: 'token', apiOrigin: url.origin }
}

/**
 * A2: the build the iPad app bundles (`vite build --mode ios`). Token mode
 * only, and its API over https: a binary cannot be pointed anywhere else once
 * built, so the loopback origin A1's tests use is refused here. There is no
 * default: a staging build names staging's API, the custom app production's.
 */
export function deviceBuild(env: Readonly<Record<string, string | undefined>>): {
  mode: 'token'
  apiOrigin: string
} {
  const build = authBuild(env)
  if (build.mode !== 'token') {
    throw new Error('the iPad build signs in with tokens: set VITE_STAFF_AUTH=token')
  }
  if (!build.apiOrigin.startsWith('https://')) {
    throw new Error(`the iPad build calls its API over https only: "${build.apiOrigin}"`)
  }
  return build
}
